import { useRef, useState } from "react"
import { Div, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faMagnifyingGlassMinus,
	faMagnifyingGlassPlus,
} from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import { clampCrop, cropLimits, cropRect, initialCrop, zoomCrop } from "../utils/logo.utils"

// quanto è largo il riquadro in cui si ritaglia: tutto il foglio, fino a un
// massimo, così sul telefono il dito ha spazio
const MAX_VIEW = 320
const viewFor = () =>
	Math.round(Math.min(MAX_VIEW, (typeof window !== "undefined" ? window.innerWidth : 400) - 72))

// quanto ingrandisce uno scatto di rotellina
const WHEEL_ZOOM = 0.0015

// le misure in cui la si rivede sotto, come starà nell'app
const PREVIEW_SIZES = [56, 38, 26]

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)

// L'immagine scelta dentro il cerchio in cui starà: un dito la sposta, due
// dita la ingrandiscono o la rimpiccioliscono, e così la rotellina o la barra
// sotto. Quello che si vede nel cerchio è quello che si salva, e sotto lo si
// rivede già piccolo, come starà nella barra o accanto a un nome.
//
// Serve alla foto del maestro e al logo del circolo (`PictureModal`):
// `circle` è il fondo del tondo in cui starà (bianco per un logo, quello
// delle iniziali per una foto) e `cover` la fa partire che riempie il
// cerchio, come si vuole una faccia.
function PictureCropper({ picked, onCancel, onConfirm, isSaving, words, circle, cover }) {
	const { url, width, height } = picked
	const [view] = useState(viewFor)
	const [crop, setCrop] = useState(() => initialCrop(width, height, view, { cover }))
	const { min, max } = cropLimits(width, height, view)

	// le dita appoggiate, e da dove è partito il gesto in corso
	const pointers = useRef(new Map())
	const gesture = useRef(null)

	const startGesture = () => {
		const [first, second] = [...pointers.current.values()]
		gesture.current = {
			crop,
			from: first,
			spread: second ? distance(first, second) : null,
		}
	}

	const onPointerDown = event => {
		event.currentTarget.setPointerCapture?.(event.pointerId)
		pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
		startGesture()
	}

	const onPointerMove = event => {
		if (!pointers.current.has(event.pointerId) || !gesture.current) return
		pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY })

		const [first, second] = [...pointers.current.values()]
		const start = gesture.current
		if (second && start.spread) {
			const nextScale = start.crop.scale * (distance(first, second) / start.spread)
			setCrop(zoomCrop(start.crop, nextScale, width, height, view))
		} else {
			setCrop(
				clampCrop(
					{
						...start.crop,
						x: start.crop.x + first.x - start.from.x,
						y: start.crop.y + first.y - start.from.y,
					},
					width,
					height,
					view,
				),
			)
		}
	}

	// alzando un dito dei due si ricomincia dall'altro, senza salti
	const onPointerUp = event => {
		pointers.current.delete(event.pointerId)
		if (pointers.current.size > 0) startGesture()
		else gesture.current = null
	}

	const zoomTo = nextScale => setCrop(current => zoomCrop(current, nextScale, width, height, view))

	const rect = cropRect(crop, width, height, view, view)

	return (
		<Div>
			<Div d="flex" justify="center" m={{ t: "1rem" }}>
				<div
					data-testid="picture-crop"
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onPointerCancel={onPointerUp}
					onWheel={event => zoomTo(crop.scale * Math.exp(-event.deltaY * WHEEL_ZOOM))}
					style={{
						position: "relative",
						width: `${view}px`,
						height: `${view}px`,
						borderRadius: "20px",
						overflow: "hidden",
						// fuori dal cerchio si vede l'immagine velata: si capisce
						// cosa resta fuori
						background: theme.colors.gunmetal_fill,
						cursor: "grab",
						// il trascinamento è nostro, non dello scorrimento del foglio
						touchAction: "none",
						userSelect: "none",
					}}>
					<div
						style={{ position: "absolute", inset: 0, borderRadius: "50%", background: circle }}
					/>
					<img
						src={url}
						alt={words.image}
						draggable={false}
						style={{
							position: "absolute",
							left: `${rect.left}px`,
							top: `${rect.top}px`,
							width: `${rect.width}px`,
							height: `${rect.height}px`,
							maxWidth: "none",
							pointerEvents: "none",
						}}
					/>
					<div
						aria-hidden="true"
						style={{
							position: "absolute",
							inset: 0,
							borderRadius: "50%",
							boxShadow: "0 0 0 999px rgba(0, 0, 0, 0.55)",
							outline: "2px solid rgba(255, 255, 255, 0.9)",
							outlineOffset: "-2px",
							pointerEvents: "none",
						}}
					/>
				</div>
			</Div>

			<Div
				d="flex"
				align="center"
				justify="center"
				m={{ t: "0.875rem" }}
				style={{ gap: "0.6rem" }}>
				<FontAwesomeIcon icon={faMagnifyingGlassMinus} color={theme.colors.text_light} size="sm" />
				{/* la barra va in scala logaritmica: ogni tratto ingrandisce
				    della stessa misura, da vicino come da lontano */}
				<input
					type="range"
					aria-label={words.zoom}
					min={Math.log(min)}
					max={Math.log(max)}
					step="any"
					value={Math.log(crop.scale)}
					onChange={event => zoomTo(Math.exp(Number(event.target.value)))}
					style={{ width: "10rem", accentColor: theme.colors.primary }}
				/>
				<FontAwesomeIcon icon={faMagnifyingGlassPlus} color={theme.colors.text_light} size="sm" />
			</Div>

			{/* come verrà: lo stesso ritaglio, alle misure dell'app */}
			<Div
				d="flex"
				align="center"
				justify="center"
				m={{ t: "0.875rem" }}
				style={{ gap: "1rem" }}
				aria-hidden="true">
				{PREVIEW_SIZES.map(size => (
					<CropPreview
						key={size}
						size={size}
						url={url}
						rect={cropRect(crop, width, height, view, size)}
						circle={circle}
					/>
				))}
				<Text textSize="caption" textColor={theme.colors.text_light}>
					come verrà
				</Text>
			</Div>

			<Div d="flex" m={{ t: "1.25rem" }} style={{ gap: "0.75rem" }}>
				<button
					type="button"
					onClick={() => !isSaving && onCancel()}
					style={{
						flex: 1,
						height: "3.25rem",
						border: 0,
						background: "transparent",
						color: theme.colors.gunmetal,
						font: "inherit",
						fontSize: "1rem",
						cursor: "pointer",
					}}>
					Annulla
				</button>
				<button
					type="button"
					onClick={() => !isSaving && onConfirm(crop, view)}
					style={{
						flex: 1,
						height: "3.25rem",
						border: 0,
						borderRadius: "15px",
						background: theme.colors.gunmetal_fill,
						color: theme.colors.white,
						font: "inherit",
						fontSize: "1rem",
						fontWeight: 500,
						cursor: "pointer",
						opacity: isSaving ? 0.7 : 1,
					}}>
					{isSaving ? words.saving : words.confirm}
				</button>
			</Div>
		</Div>
	)
}

function CropPreview({ size, url, rect, circle }) {
	return (
		<span
			style={{
				position: "relative",
				width: `${size}px`,
				height: `${size}px`,
				flexShrink: 0,
				borderRadius: "50%",
				overflow: "hidden",
				background: circle,
			}}>
			<img
				src={url}
				alt=""
				draggable={false}
				style={{
					position: "absolute",
					left: `${rect.left}px`,
					top: `${rect.top}px`,
					width: `${rect.width}px`,
					height: `${rect.height}px`,
					maxWidth: "none",
				}}
			/>
		</span>
	)
}

export default PictureCropper
